import type { ContentRegistry } from '../content/registry';
import type { CharacterId } from '../content/characters';
import type { StatMod } from '../content/types';
import type { SkillFlag } from '../content/skills';
import type { RunConfig, Sim } from '../sim/sim';
import { deserializeSnapshot, serializeSnapshot, snapshot, type SimSnapshot } from '../sim/snapshot';

export const RUN_SAVE_KEY = 'chronofall.run.v1';
export const RUN_SAVE_VERSION = 1;

export interface StoredRunConfig {
  seed: number; playerCount: number; startEra: number; paradox: number;
  metaMods: StatMod[]; equipment: string[][]; characters: CharacterId[];
  rerolls: number; banishes: number; unlocked: string[];
  pets?: (string | null)[]; petLevels?: number[]; startingHeals?: number[]; challengeId?: string;
  /** Online co-op per-player overrides (see RunConfig). */
  metaModsByPlayer?: StatMod[][]; unlockedByPlayer?: string[][];
  skillFlags?: SkillFlag[][];
  rerollsByPlayer?: number[]; banishesByPlayer?: number[];
}

export interface StoredRun {
  version: 1;
  gameVersion: string;
  savedAt: number;
  config: StoredRunConfig;
  snapshot: string;
  replay?: unknown;
}

export function storeConfig(config: RunConfig): StoredRunConfig {
  const stored: StoredRunConfig = { ...config, equipment: config.equipment.map((items) => items.map((item) => item.id)), characters: [...(config.characters ?? [])], unlocked: [...config.unlocked], unlockedByPlayer: undefined };
  if (config.unlockedByPlayer) stored.unlockedByPlayer = config.unlockedByPlayer.map((set) => [...set]);
  else delete stored.unlockedByPlayer;
  return stored;
}

export function restoreConfig(stored: StoredRunConfig, reg: ContentRegistry): RunConfig {
  const config: RunConfig = { ...stored, equipment: stored.equipment.map((ids) => ids.map((id) => reg.items.get(id)).filter((item) => item !== undefined)), unlocked: new Set(stored.unlocked), unlockedByPlayer: undefined };
  if (stored.unlockedByPlayer) config.unlockedByPlayer = stored.unlockedByPlayer.map((ids) => new Set(ids));
  else delete config.unlockedByPlayer;
  return config;
}

export function saveRun(sim: Sim, gameVersion: string, replay?: unknown, storage: Storage = localStorage): boolean {
  try {
    const data: StoredRun = { version: 1, gameVersion, savedAt: Date.now(), config: storeConfig(sim.config), snapshot: serializeSnapshot(snapshot(sim)), replay };
    storage.setItem(RUN_SAVE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.warn('run save failed', error);
    return false;
  }
}

export function loadRun(gameVersion: string, storage: Storage = localStorage): StoredRun | null {
  try {
    const raw = storage.getItem(RUN_SAVE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredRun;
    if (data.version !== RUN_SAVE_VERSION || data.gameVersion !== gameVersion) { clearRun(storage); return null; }
    deserializeSnapshot(data.snapshot); // validate before advertising Continue
    return data;
  } catch (error) {
    console.warn('run load failed; discarding', error);
    clearRun(storage);
    return null;
  }
}

export function storedSnapshot(run: StoredRun): SimSnapshot { return deserializeSnapshot(run.snapshot); }
export function clearRun(storage: Storage = localStorage): void {
  try { storage.removeItem(RUN_SAVE_KEY); } catch { /* storage can be disabled */ }
}
